import { Key, useMemo, memo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { AdminFormRow, AdminFormSection } from '@ui';
import { TXUserFormBase } from '@lp/features/admin/forms/TXUserFormBase/TXUserFormBase';
import { TMDXFile } from '@/api/apis/files';
import { getFullName } from '@/utils/helpers';
import { CreateTXUserFormValues } from '@/types/customer';
import { CustomerMT } from '@/api/apis/customer';
import { getContactList, getRolesAndPermissions } from './utils';
import { ControlledSelect } from '../components/inputs/ControlledSelect';

export type PrimaryTXUserFormProps = {
  isReadOnly?: boolean;
  list?: CustomerMT[];
  isDisabled?: boolean;
  txCenterId?: string;
};

export const PrimaryTXUserForm = memo(
  ({ list, isReadOnly = false, isDisabled = false, txCenterId }: PrimaryTXUserFormProps) => {
    const { control, setValue } = useFormContext<CreateTXUserFormValues & { primaryTXUserId?: string }>();
    const photoPath = useWatch({ control, name: 'photoPath' });
    const firstName = useWatch({ control, name: 'firstName' });
    const lastName = useWatch({ control, name: 'lastName' });

    const contactList = useMemo(() => getContactList(list), [list]);

    const onTXUserChange = (txUserId: Key | null) => {
      if (list) {
        const newTXUser = list.find((c) => c.id === txUserId);

        if (newTXUser) {
          const { roles, permissions, currentRoles } = getRolesAndPermissions(txCenterId, newTXUser);

          setValue('roles', [], { shouldDirty: true, shouldValidate: true });
          setValue('permissions', [], { shouldDirty: true, shouldValidate: true });
          setValue('roles', roles, { shouldDirty: true, shouldValidate: true });
          setValue('permissions', permissions, { shouldDirty: true, shouldValidate: true });
          setValue('primaryTXUserId', newTXUser.id, { shouldDirty: true, shouldValidate: true });
          setValue('firstName', newTXUser.firstName, { shouldDirty: true, shouldValidate: true });
          setValue('lastName', newTXUser.lastName, { shouldDirty: true, shouldValidate: true });
          setValue('photoPath', newTXUser.photoPath || '');
          setValue('email', newTXUser.email, { shouldDirty: true, shouldValidate: true });
          setValue('primaryPhone', newTXUser.primaryPhone, { shouldDirty: true, shouldValidate: true });
          setValue('roles', currentRoles || []);
          setValue('secondaryPhone', newTXUser.secondaryPhone ?? '', { shouldDirty: true, shouldValidate: true });
          setValue('photo', undefined);
        }
      }
    };

    const onImageChange = (file: TMDXFile) => {
      setValue('photo', file);
    };

    return (
      <AdminFormSection
        title="Primary Contact Details"
        withAvatar
        imageUrl={photoPath}
        isReadOnly={isReadOnly || isDisabled}
        name={getFullName(firstName, lastName)}
        onImageChange={onImageChange}
        startContent={
          !!list && (
            <AdminFormRow title="Select contact" isRequired inputWrapperClass="max-w-190">
              <ControlledSelect
                name="primaryTXUserId"
                items={contactList}
                placeholder="Choose contact"
                aria-label="Primary Contact"
                isDisabled={isDisabled}
                onChange={(e) => onTXUserChange(e.target.value)}
              />
            </AdminFormRow>
          )
        }
        type="user"
      >
        <TXUserFormBase isHidden={false} isReadOnly={isReadOnly || isDisabled} />
      </AdminFormSection>
    );
  },
);
